import { useEffect, useState } from 'react'
import {
  Button,
  Code,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { IconCalendarRepeat } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { CreateRecurringRequest, RecurringTemplateDto } from '@ledokol/contracts'
import { createRecurring, updateRecurring } from '../../shared/api/recurring'
import { fetchProject } from '../../shared/api/projects'
import { isApiError } from '../../shared/api/client'

/**
 * Пресеты задают частоту И тип дедлайна ОДНОЙ сущностью. Поле due_offset.kind
 * наружу не торчит — оно выводится из пресета (TD-1: фронт генерит когерентную
 * пару rrule+due_offset, сервер страхует кросс-валидацией). Свободного RRULE нет.
 */
type Preset = 'weekly' | 'monthly'

const WEEKDAYS: { value: string; label: string }[] = [
  { value: 'MO', label: 'Понедельник' },
  { value: 'TU', label: 'Вторник' },
  { value: 'WE', label: 'Среда' },
  { value: 'TH', label: 'Четверг' },
  { value: 'FR', label: 'Пятница' },
  { value: 'SA', label: 'Суббота' },
  { value: 'SU', label: 'Воскресенье' },
]
const WEEKDAY_LABEL = Object.fromEntries(WEEKDAYS.map((w) => [w.value, w.label]))

const TIMES = ['09:00', '12:00', '15:00', '17:00', '18:00', '19:00']
const PRIORITIES = [
  { value: 'low', label: 'Низкий' },
  { value: 'med', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
]

interface Props {
  projectId: string
  opened: boolean
  onClose: () => void
  editing: RecurringTemplateDto | null
}

export function RecurringFormModal({ projectId, opened, onClose, editing }: Props) {
  const qc = useQueryClient()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [preset, setPreset] = useState<Preset>('weekly')
  const [weekday, setWeekday] = useState('FR')
  const [workingDay, setWorkingDay] = useState<number>(3)
  const [time, setTime] = useState('18:00')
  const [createDaysBefore, setCreateDaysBefore] = useState<number>(3)
  const [priority, setPriority] = useState('med')
  const [assigneeId, setAssigneeId] = useState<string | null>(null)

  // Сброс/предзаполнение при открытии (create — дефолты; edit — из шаблона)
  useEffect(() => {
    if (!opened) return
    if (editing) {
      setTitle(editing.title_template)
      setDescription(editing.description_template ?? '')
      setTime(editing.due_offset.time)
      setCreateDaysBefore(editing.create_days_before)
      setPriority(editing.priority)
      setAssigneeId(editing.assignee?.id ?? null)
      if (editing.due_offset.kind === 'weekday') {
        setPreset('weekly')
        setWeekday(editing.due_offset.value)
      } else if (editing.due_offset.kind === 'business_days_after_month_end') {
        setPreset('monthly')
        setWorkingDay(editing.due_offset.value)
      }
    } else {
      setTitle('')
      setDescription('')
      setPreset('weekly')
      setWeekday('FR')
      setWorkingDay(3)
      setTime('18:00')
      setCreateDaysBefore(3)
      setPriority('med')
      setAssigneeId(null)
    }
  }, [opened, editing])

  // Исполнитель — участники выбранного проекта (семантически корректно)
  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProject(projectId),
    enabled: opened && !!projectId,
  })
  const assigneeOptions =
    projectQ.data?.members.map((m) => ({ value: m.user.id, label: m.user.full_name })) ?? []

  function buildPayload(): CreateRecurringRequest {
    const base = {
      title_template: title.trim(),
      description_template: description.trim() || null,
      assignee_id: assigneeId,
      priority: priority as CreateRecurringRequest['priority'],
      create_days_before: createDaysBefore,
    }
    if (preset === 'weekly') {
      return {
        ...base,
        rrule: `FREQ=WEEKLY;BYDAY=${weekday}`,
        due_offset: { kind: 'weekday', value: weekday, time },
      }
    }
    return {
      ...base,
      rrule: 'FREQ=MONTHLY;BYMONTHDAY=1',
      due_offset: { kind: 'business_days_after_month_end', value: workingDay, time },
    }
  }

  const save = useMutation({
    mutationFn: () =>
      editing ? updateRecurring(editing.id, buildPayload()) : createRecurring(projectId, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring', projectId] })
      notifications.show({
        color: 'teal',
        title: editing ? 'Регулярка обновлена' : 'Регулярка создана',
        message: title.trim(),
      })
      onClose()
    },
    onError: (e) => {
      const msg = isApiError(e)
        ? e.response?.status === 403
          ? 'Недостаточно прав для управления регулярками этого проекта'
          : (e.response?.data.error.message ?? 'Не удалось сохранить')
        : 'Не удалось сохранить'
      notifications.show({ color: 'red', title: 'Ошибка', message: msg })
    },
  })

  // Превью — человекочитаемое + сгенерированный RRULE (read-only, НЕ ввод)
  const payload = buildPayload()
  const humanSchedule =
    preset === 'weekly'
      ? `Каждую неделю · ${WEEKDAY_LABEL[weekday]} · дедлайн в ${time}`
      : `Каждый месяц · дедлайн на ${workingDay}-й рабочий день · в ${time}`

  const canSubmit = title.trim().length > 0 && (preset === 'weekly' ? !!weekday : workingDay >= 1)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={
        <Group gap="xs">
          <IconCalendarRepeat size={18} />
          <Text fw={700}>{editing ? 'Редактировать регулярку' : 'Новая регулярная задача'}</Text>
        </Group>
      }
    >
      <Stack gap="md">
        <TextInput
          label="Шаблон названия"
          description="Переменные: {iso_week} — номер недели, {date} — дата срока"
          placeholder="Например: Недельный отчёт KW {iso_week}"
          required
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />

        <Select
          label="Периодичность"
          description="Тип дедлайна задаётся периодичностью — отдельно его выбирать не нужно"
          data={[
            { value: 'weekly', label: 'Еженедельная' },
            { value: 'monthly', label: 'Ежемесячная (N-й рабочий день)' },
          ]}
          value={preset}
          onChange={(v) => setPreset((v as Preset) ?? 'weekly')}
          allowDeselect={false}
        />

        {/* Поля привязаны к периодичности — несогласованную пару не собрать */}
        {preset === 'weekly' ? (
          <Select
            label="День недели"
            data={WEEKDAYS}
            value={weekday}
            onChange={(v) => setWeekday(v ?? 'FR')}
            allowDeselect={false}
          />
        ) : (
          <NumberInput
            label="Рабочий день месяца"
            description="Дедлайн — N-й рабочий день месяца (праздники/выходные пропускаются)"
            min={1}
            max={23}
            value={workingDay}
            onChange={(v) => setWorkingDay(typeof v === 'number' ? v : 3)}
          />
        )}

        <Group grow>
          <Select
            label="Время дедлайна"
            data={TIMES}
            value={time}
            onChange={(v) => setTime(v ?? '18:00')}
            allowDeselect={false}
          />
          <NumberInput
            label="Создавать за (дней до срока)"
            min={0}
            max={60}
            value={createDaysBefore}
            onChange={(v) => setCreateDaysBefore(typeof v === 'number' ? v : 3)}
          />
        </Group>

        <Group grow>
          <Select
            label="Приоритет"
            data={PRIORITIES}
            value={priority}
            onChange={(v) => setPriority(v ?? 'med')}
            allowDeselect={false}
          />
          <Select
            label="Исполнитель"
            placeholder={projectQ.isLoading ? 'Загрузка…' : 'Не назначен'}
            data={assigneeOptions}
            value={assigneeId}
            onChange={setAssigneeId}
            clearable
            searchable
          />
        </Group>

        <Textarea
          label="Описание (необязательно)"
          autosize
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />

        <Stack gap={4} p="sm" style={{ background: 'var(--mantine-color-blue-0)', borderRadius: 8 }}>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            Что будет создаваться
          </Text>
          <Text size="sm">{humanSchedule}</Text>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              Правило (генерируется автоматически):
            </Text>
            <Code>{payload.rrule}</Code>
          </Group>
        </Stack>

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSubmit}>
            {editing ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
